import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  CAREER_CHALLENGE_IP_POLICY_LABELS,
  CareerChallengeDetailResponse,
  CareerChallengeFaqItemDto,
  CareerChallengeIpPolicy,
  CareerChallengeJudgeDto,
  CareerChallengePrizeTierDto,
  CareerChallengeSponsorDto
} from '../../models';
import { presentCareerChallenge } from '../career-challenge-status/career-challenge-status.util';
import { renderLessonMarkdown } from '../lesson-markdown.util';
import {
  ChallengeMediaPlaceholder,
  onChallengeImageError,
  resolveChallengeMediaSrc
} from '../challenge-media/challenge-media.util';

export type CareerChallengeBriefMode = 'learner' | 'preview';
export type CareerChallengeBriefTab = 'overview' | 'skills' | 'faq' | 'results';

@Component({
  selector: 'app-career-challenge-brief',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './career-challenge-brief.component.html',
  styleUrl: './career-challenge-brief.component.scss'
})
export class CareerChallengeBriefComponent {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) challenge!: CareerChallengeDetailResponse;
  @Input() mode: CareerChallengeBriefMode = 'learner';
  @Input() showFreeRemediationBenefit = false;
  /** Show Results tab (learner mode when a submission exists). */
  @Input() showResultsTab = false;
  @Output() readonly tabChange = new EventEmitter<CareerChallengeBriefTab>();

  activeTab: CareerChallengeBriefTab = 'overview';

  get isPreview(): boolean {
    return this.mode === 'preview';
  }

  get faqItems(): CareerChallengeFaqItemDto[] {
    return [...(this.challenge?.faqItems ?? [])]
      .filter((item) => item.question?.trim() && item.answer?.trim())
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  get ipPolicyLabel(): string {
    const key = (this.challenge?.ipPolicy ?? 'participants_retain') as CareerChallengeIpPolicy;
    return CAREER_CHALLENGE_IP_POLICY_LABELS[key] ?? CAREER_CHALLENGE_IP_POLICY_LABELS.participants_retain;
  }

  get descriptionHtml(): SafeHtml | null {
    const markdown = this.challenge?.description?.trim();
    if (!markdown) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustHtml(renderLessonMarkdown(markdown));
  }

  get hasSkillMappings(): boolean {
    return (this.challenge?.skillMappings?.length ?? 0) > 0;
  }

  get hasSkillsAssessed(): boolean {
    return (this.challenge?.skillsAssessed?.length ?? 0) > 0;
  }

  get skillGroups(): {
    skillId: string;
    skillName: string;
    topics: {
      skillId: string;
      topicName: string;
      targetProficiency?: number | string | null;
      source?: string;
    }[];
  }[] {
    const mappings = this.challenge?.skillMappings ?? [];
    const bySkill = new Map<
      string,
      {
        skillId: string;
        skillName: string;
        topics: {
          skillId: string;
          topicName: string;
          targetProficiency?: number | string | null;
          source?: string;
        }[];
      }
    >();

    for (const mapping of mappings) {
      const skillId = (mapping.skillId || mapping.skillName || '').trim() || 'unknown';
      const skillName = (mapping.skillName || mapping.topicName || 'Skill').trim();
      let group = bySkill.get(skillId);
      if (!group) {
        group = { skillId, skillName, topics: [] };
        bySkill.set(skillId, group);
      }
      const topicName = (mapping.topicName || skillName).trim();
      group.topics.push({
        skillId,
        topicName,
        targetProficiency: mapping.targetProficiency,
        source: mapping.source
      });
    }

    return [...bySkill.values()];
  }

  private get registrationPresentation() {
    return presentCareerChallenge(this.challenge);
  }

  get challengeStatusLabel(): string {
    return this.registrationPresentation.statusLabel;
  }

  get registrationStatusLabel(): string {
    return this.registrationPresentation.registrationLabel;
  }

  get showRegistrationStatusLine(): boolean {
    return this.registrationPresentation.showRegistrationLabel;
  }

  get participationSummary(): string {
    return this.registrationPresentation.participationSummary;
  }

  get audienceRoleLabel(): string {
    const audience = this.challenge?.audience;
    return (audience?.careerRoleName || audience?.careerRoleSlug || '').trim();
  }

  get audienceSpecializationLabel(): string {
    const audience = this.challenge?.audience;
    return (audience?.specializationName || audience?.specializationSlug || '').trim();
  }

  get keyDateRows(): { label: string; value: string }[] {
    const mode = (this.challenge?.access?.mode ?? '').toString();
    if (mode === 'first_sga_evaluation' || mode === 'personalized_progression') {
      return [];
    }
    const schedule = this.challenge?.schedule;
    const rows: { label: string; value: string }[] = [];
    const add = (label: string, raw: Date | string | null | undefined) => {
      const formatted = this.formatKeyDate(raw);
      if (formatted) {
        rows.push({ label, value: formatted });
      }
    };
    add('Registration opens', this.challenge?.registrationOpensAt ?? schedule?.registrationOpensAt);
    add('Registration closes', this.challenge?.registrationClosesAt ?? schedule?.registrationClosesAt);
    add('Starts', this.challenge?.startsAt ?? schedule?.startsAt);
    add('Ends', this.challenge?.endsAt ?? schedule?.endsAt);
    add('Conversation closes', schedule?.comprehensionEndsAt);
    add('Judging ends', schedule?.judgingEndsAt);
    return rows;
  }

  selectTab(tab: CareerChallengeBriefTab): void {
    if (tab === 'results' && !this.showResultsTab) {
      return;
    }
    this.activeTab = tab;
    this.tabChange.emit(tab);
  }

  /** Parent can open Results after scoring. */
  openResultsTab(): void {
    if (!this.showResultsTab) {
      return;
    }
    this.selectTab('results');
  }

  formatKeyDate(value: Date | string | null | undefined): string | null {
    if (value == null || value === '') {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  proficiencyLabel(value: number | string | null | undefined): string {
    const n = typeof value === 'number' ? value : Number(value);
    switch (n) {
      case 1:
        return 'Beginner';
      case 2:
        return 'Intermediate';
      case 3:
        return 'Advanced';
      case 4:
        return 'Expert';
      default:
        return typeof value === 'string' && value.trim() ? value : 'None';
    }
  }

  coverSrc(): string {
    return resolveChallengeMediaSrc(
      this.challenge.coverDisplayUrl,
      this.challenge.coverImageUrl,
      'cover'
    );
  }

  judgeSrc(judge: CareerChallengeJudgeDto): string {
    return resolveChallengeMediaSrc(judge.photoDisplayUrl, judge.photoUrl, 'judge');
  }

  sponsorSrc(sponsor: CareerChallengeSponsorDto): string {
    return resolveChallengeMediaSrc(sponsor.logoDisplayUrl, sponsor.logoUrl, 'sponsor');
  }

  prizeSrc(prize: CareerChallengePrizeTierDto): string {
    return resolveChallengeMediaSrc(prize.photoDisplayUrl, prize.photoUrl, 'prize');
  }

  onImageError(event: Event, placeholder: ChallengeMediaPlaceholder): void {
    onChallengeImageError(event, placeholder);
  }
}